import { Box } from "@chakra-ui/react";
import DefaultLayout from "@/components/layouts/DefaultLayout";
import { Head } from "@inertiajs/react";
import { SEOHead } from "@/components/SEOHead";
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Features } from "./components/Features";
import { HowItWorks } from "./components/HowItWorks";
import { FAQ } from "./components/FAQ";
import { Footer } from "./components/Footer";

// Theme type
type ThemeConfig = {
  mode: "light" | "dark";
  primary: string;
  primaryHover: string;
  primaryDark: string;
  primaryGlow: string;
  bg: string;
  surface: string;
  surfaceMuted: string;
  textPrimary: string;
  textSecondary: string;
  border: string;
  navBg: string;
  cardBg: string;
};

// Theme configurations
const darkTheme: ThemeConfig = {
  mode: "dark",
  primary: "#2e8e6b",
  primaryHover: "#257a5c",
  primaryDark: "#1a5e45",
  primaryGlow: "rgba(46, 142, 107, 0.5)",
  bg: "#0f1412",
  surface: "#17201c",
  surfaceMuted: "#25302a",
  textPrimary: "#ffffff",
  textSecondary: "#9ca3af",
  border: "rgba(255, 255, 255, 0.1)",
  navBg: "rgba(15, 20, 18, 0.85)",
  cardBg: "#17201c",
};

const lightTheme: ThemeConfig = {
  mode: "light",
  primary: "#2e8e6b",
  primaryHover: "#257a5c",
  primaryDark: "#1a5e45",
  primaryGlow: "rgba(46, 142, 107, 0.3)",
  bg: "#f8faf9",
  surface: "#ffffff",
  surfaceMuted: "#f1f5f3",
  textPrimary: "#1a1a1a",
  textSecondary: "#6b7280",
  border: "#e5e7eb",
  navBg: "rgba(255, 255, 255, 0.9)",
  cardBg: "#ffffff",
};

export type LandingTheme = ThemeConfig;

// Theme context
const LandingThemeContext = createContext<{
  theme: LandingTheme;
  toggleTheme: () => void;
  isDark: boolean;
}>({
  theme: lightTheme,
  toggleTheme: () => {},
  isDark: false,
});

export const useLandingTheme = () => useContext(LandingThemeContext);

// Export for backwards compatibility
export const landingTheme = lightTheme;

const Home = () => {
  const [showNavbar, setShowNavbar] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const footerRef = useRef<{ focusInput: () => void }>(null);

  const handleGetAccess = () => {
    footerRef.current?.focusInput();
  };

  // Load theme preference from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("muro-landing-theme");
    if (stored === "light") {
      setIsDark(false);
    }
  }, []);

  // Save theme preference to localStorage
  const toggleTheme = () => {
    setIsDark((prev) => {
      const newValue = !prev;
      localStorage.setItem("muro-landing-theme", newValue ? "dark" : "light");
      return newValue;
    });
  };

  const currentTheme = isDark ? darkTheme : lightTheme;

  useEffect(() => {
    const heroEl = document.getElementById("hero");
    if (!heroEl) {
      setShowNavbar(true);
      return;
    }

    const IntersectionObserverCtor = (window as unknown as { IntersectionObserver?: typeof IntersectionObserver })
      .IntersectionObserver;
    if (IntersectionObserverCtor) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setShowNavbar(!entry.isIntersecting);
        },
        { root: null, threshold: 0 },
      );

      observer.observe(heroEl);
      return () => observer.disconnect();
    }

    const handleScroll = () => {
      const rect = heroEl.getBoundingClientRect();
      setShowNavbar(rect.bottom <= 0);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <LandingThemeContext.Provider value={{ theme: currentTheme, toggleTheme, isDark }}>
      <SEOHead
        title="Muro - Automated Focus Time Blocking for Deep Work"
        description="Muro automatically finds gaps in your calendar and blocks focus time for deep work. Reclaim 5-15 hours of productive time weekly. Join the beta waitlist today."
        canonical="/"
      />
      <Head>
        {/* Structured Data - Organization & WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Muro",
            url: "https://getmuro.com",
            logo: "https://getmuro.com/android-chrome-512x512.png",
            description:
              "Automated focus time blocking for deep work. Find and protect productive time in your calendar.",
            sameAs: ["https://twitter.com/getmuro", "https://linkedin.com/company/getmuro"],
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "Customer Support",
              email: "support@getmuro.com",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Muro",
            url: "https://getmuro.com",
            applicationCategory: "ProductivityApplication",
            operatingSystem: "Web",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            description:
              "Muro automatically finds gaps in your calendar and blocks focus time for deep work. Reclaim 5-15 hours of productive time weekly.",
            featureList: [
              "Automated calendar analysis",
              "Deep work block scheduling",
              "Multi-calendar support",
              "Smart time detection",
              "Google Calendar integration",
            ],
          })}
        </script>
      </Head>
      <Box
        fontFamily="'Space Grotesk', sans-serif"
        bg={currentTheme.bg}
        color={currentTheme.textPrimary}
        overflowX="hidden"
        minH="100vh"
        transition="background-color 0.3s, color 0.3s"
      >
        {showNavbar && <Navbar onGetAccess={handleGetAccess} />}
        <Hero onGetAccess={handleGetAccess} />
        <Features />
        <HowItWorks />
        <FAQ />
        <Footer ref={footerRef} />
      </Box>
    </LandingThemeContext.Provider>
  );
};

Home.layout = (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>;

export default Home;
